import { useEffect, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  LinearProgress,
  Box,
  Typography,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import type { UpdateErrorDebugPayload } from '../../types/electron';

type UpdateState =
  | { phase: 'idle' }
  | { phase: 'no-update' }
  | { phase: 'available'; version: string }
  | { phase: 'downloading'; version: string; percent: number }
  | { phase: 'ready'; version: string }
  | { phase: 'error'; message: string };

export function UpdateNotification() {
  const [state, setState] = useState<UpdateState>({ phase: 'idle' });
  const [open, setOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    const api = window.electronAPI;
    if (!api) return;

    const unsubAvailable = api.onUpdateAvailable(({ version }) => {
      setIsRetrying(false);
      setState({ phase: 'available', version });
      setOpen(true);
    });

    const unsubNotAvailable = api.onUpdateNotAvailable(({ reason }) => {
      setIsRetrying(false);
      // Avoid noisy toasts for automatic background checks.
      if (reason !== 'manual') {
        return;
      }
      setState({ phase: 'no-update' });
      setOpen(true);
    });

    const unsubDownloading = api.onUpdateDownloading(({ percent }) => {
      setIsRetrying(false);
      setState((prev) => ({
        phase: 'downloading',
        version:
          prev.phase === 'available' || prev.phase === 'downloading'
            ? prev.version
            : '',
        percent,
      }));
      setOpen(true);
    });

    const unsubDownloaded = api.onUpdateDownloaded(({ version }) => {
      setIsRetrying(false);
      setState({ phase: 'ready', version });
      setOpen(true);
    });

    const unsubError = api.onUpdateError(() => {
      setIsRetrying(false);
      console.error('[AutoUpdate] Update check failed: autoupdate error');
      setState({ phase: 'error', message: 'autoupdate error' });
      setOpen(true);
    });

    const unsubErrorDebug = api.onUpdateErrorDebug(
      (payload: UpdateErrorDebugPayload) => {
        console.error('[AutoUpdate][Debug]', {
          stage: payload.stage,
          code: payload.code,
          message: payload.message,
          timestamp: payload.timestamp,
          channel: payload.channel,
          appVersion: payload.appVersion,
          updateServerUrl: payload.updateServerUrl,
          stack: payload.stack,
        });
      }
    );

    return () => {
      unsubAvailable();
      unsubNotAvailable();
      unsubDownloading();
      unsubDownloaded();
      unsubError();
      unsubErrorDebug();
    };
  }, []);

  const handleDismiss = useCallback(() => {
    setOpen(false);
    window.electronAPI?.dismissUpdate();
  }, []);

  const handleApply = useCallback(() => {
    window.electronAPI?.applyUpdate();
  }, []);

  const handleCloseError = useCallback(() => {
    setIsRetrying(false);
    setOpen(false);
    setState({ phase: 'idle' });
  }, []);

  const handleRetryUpdate = useCallback(() => {
    setIsRetrying(true);
    console.info('[AutoUpdate] Manual retry triggered');
    const api = window.electronAPI;
    if (api) {
      if (typeof api.checkForUpdates === 'function') {
        api.checkForUpdates();
      } else {
        api.retryUpdateCheck();
      }
    }
    setOpen(false);
    setState({ phase: 'idle' });
  }, []);

  if (state.phase === 'idle') return null;

  if (state.phase === 'error') {
    return (
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleCloseError}
      >
        <Alert
          severity="warning"
          onClose={handleCloseError}
          sx={{
            width: 420,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main',
            backgroundColor: 'background.paper',
            backdropFilter: 'blur(8px)',
            '& .MuiAlert-message': {
              width: '100%',
              minWidth: 0,
            },
          }}
          action={
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={handleRetryUpdate}
              disabled={isRetrying}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                minWidth: 72,
              }}
            >
              {isRetrying ? 'Retrying' : 'Retry'}
            </Button>
          }
        >
          <AlertTitle sx={{ mb: 0.25, fontWeight: 700 }}>
            Update check failed
          </AlertTitle>
          <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
            {state.message}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.82 }}>
            No update was installed. You can retry now.
          </Typography>
        </Alert>
      </Snackbar>
    );
  }

  if (state.phase === 'no-update') {
    return (
      <Snackbar
        open={open}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleCloseError}
      >
        <Alert
          severity="info"
          icon={<SystemUpdateAltIcon fontSize="inherit" />}
          onClose={handleCloseError}
          sx={{ maxWidth: 360 }}
        >
          <AlertTitle>No updates available</AlertTitle>
          You are already on the latest version.
        </Alert>
      </Snackbar>
    );
  }

  if (state.phase === 'available') {
    return (
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          icon={<SystemUpdateAltIcon fontSize="inherit" />}
          action={
            <Button color="inherit" size="small" onClick={handleDismiss}>
              Later
            </Button>
          }
          onClose={handleDismiss}
          sx={{ maxWidth: 360 }}
        >
          <AlertTitle>Update available — v{state.version}</AlertTitle>
          Downloading in the background…
        </Alert>
      </Snackbar>
    );
  }

  if (state.phase === 'downloading') {
    return (
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          icon={<SystemUpdateAltIcon fontSize="inherit" />}
          sx={{ maxWidth: 360 }}
        >
          <AlertTitle>Downloading update — v{state.version}</AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={state.percent}
                sx={{ borderRadius: 1 }}
              />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 36 }}>
              {Math.round(state.percent)}%
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    );
  }

  // phase === 'ready'
  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        severity="success"
        icon={<SystemUpdateAltIcon fontSize="inherit" />}
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={handleDismiss}
              sx={{ mr: 0.5 }}
            >
              Later
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleApply}
            >
              Restart now
            </Button>
          </>
        }
        sx={{ maxWidth: 400 }}
      >
        <AlertTitle>v{state.version} ready to install</AlertTitle>
        Restart Studio Vault to apply the update.
      </Alert>
    </Snackbar>
  );
}
